import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Send, User, Calendar, Globe, MapPin, Ruler, Video } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../styles/scouting.css';

const Scouting = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    birth_date: '',
    nationality: '',
    position: '',
    current_club: '',
    contact_email: '',
    contact_phone: '',
    height: '',
    dominant_foot: '',
    video_url: '',
    additional_info: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/scouting`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Error al enviar el perfil');
      }
      
      toast.success("Perfil enviado correctamente", {
        description: "Nuestro equipo revisará tu información y se pondrá en contacto contigo.",
      });
      
      // Reset form
      setFormData({
        full_name: '',
        birth_date: '',
        nationality: '',
        position: '',
        current_club: '',
        contact_email: '',
        contact_phone: '',
        height: '',
        dominant_foot: '',
        video_url: '',
        additional_info: ''
      });
    } catch (error) {
      toast.error("Error al enviar perfil", {
        description: "Por favor intenta nuevamente.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="scouting-page">
      <header className="scouting-header">
        <nav className="scouting-nav">
          <Link to="/" className="back-link">
            <ArrowLeft className="back-icon" />
            <span>Volver a Inicio</span>
          </Link>
          <img src="/ikons-logo.png" alt="Ikons Agency" className="scouting-logo" />
        </nav>
      </header>

      <main className="scouting-main">
        <div className="scouting-container">
          <div className="scouting-intro">
            <h1 className="scouting-title">ENVÍO DE PERFIL</h1>
            <p className="scouting-subtitle">
              ¿Eres jugador o jugadora con proyección internacional? Completa el formulario 
              y nuestro equipo de scouting evaluará tu perfil.
            </p>
            <div className="scouting-note">
              <p>Evaluamos criterios como: ambición, disciplina, mentalidad profesional y proyección real.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="scouting-form" data-testid="scouting-form">
            <div className="form-section">
              <h3 className="form-section-title">
                <User className="section-icon" />
                Información Personal
              </h3>
              
              <div className="form-row">
                <div className="form-group">
                  <Label htmlFor="full_name">Nombre completo *</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    placeholder="Tu nombre completo"
                    required
                    data-testid="scouting-name"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="birth_date">Fecha de nacimiento *</Label>
                  <Input
                    id="birth_date"
                    name="birth_date"
                    type="date"
                    value={formData.birth_date}
                    onChange={handleInputChange}
                    required
                    data-testid="scouting-birthdate"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <Label htmlFor="nationality">Nacionalidad *</Label>
                  <Input
                    id="nationality"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleInputChange}
                    placeholder="Ej: Español, Mexicano, Colombiano"
                    required
                    data-testid="scouting-nationality"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="height">Altura</Label>
                  <Input
                    id="height"
                    name="height"
                    value={formData.height}
                    onChange={handleInputChange}
                    placeholder="Ej: 1.78m"
                    data-testid="scouting-height"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="form-section-title">
                <MapPin className="section-icon" />
                Información Deportiva
              </h3>
              
              <div className="form-row">
                <div className="form-group">
                  <Label htmlFor="position">Posición *</Label>
                  <Select 
                    name="position" 
                    onValueChange={(value) => handleSelectChange('position', value)}
                    required
                  >
                    <SelectTrigger data-testid="scouting-position">
                      <SelectValue placeholder="Selecciona tu posición" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="portero">Portero/a</SelectItem>
                      <SelectItem value="defensa_central">Defensa Central</SelectItem>
                      <SelectItem value="lateral_derecho">Lateral Derecho</SelectItem>
                      <SelectItem value="lateral_izquierdo">Lateral Izquierdo</SelectItem>
                      <SelectItem value="mediocentro_defensivo">Mediocentro Defensivo</SelectItem>
                      <SelectItem value="mediocentro">Mediocentro</SelectItem>
                      <SelectItem value="mediocentro_ofensivo">Mediocentro Ofensivo</SelectItem>
                      <SelectItem value="extremo_derecho">Extremo Derecho</SelectItem>
                      <SelectItem value="extremo_izquierdo">Extremo Izquierdo</SelectItem>
                      <SelectItem value="delantero_centro">Delantero Centro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-group">
                  <Label htmlFor="dominant_foot">Pie dominante</Label>
                  <Select 
                    name="dominant_foot" 
                    onValueChange={(value) => handleSelectChange('dominant_foot', value)}
                  >
                    <SelectTrigger data-testid="scouting-foot">
                      <SelectValue placeholder="Selecciona" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="derecho">Derecho</SelectItem>
                      <SelectItem value="izquierdo">Izquierdo</SelectItem>
                      <SelectItem value="ambidiestro">Ambidiestro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="form-group">
                <Label htmlFor="current_club">Club actual</Label>
                <Input
                  id="current_club"
                  name="current_club"
                  value={formData.current_club}
                  onChange={handleInputChange}
                  placeholder="Nombre de tu club actual (si aplica)"
                  data-testid="scouting-club"
                />
              </div>
            </div>

            <div className="form-section">
              <h3 className="form-section-title">
                <Video className="section-icon" />
                Material y Contacto
              </h3>
              
              <div className="form-row">
                <div className="form-group">
                  <Label htmlFor="contact_email">Email *</Label>
                  <Input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={handleInputChange}
                    placeholder="tu@email.com"
                    required
                    data-testid="scouting-email"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="contact_phone">Teléfono / WhatsApp</Label>
                  <Input
                    id="contact_phone"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleInputChange}
                    placeholder="+34 XXX XXX XXX"
                    data-testid="scouting-phone"
                  />
                </div>
              </div>

              <div className="form-group">
                <Label htmlFor="video_url">Enlace a video highlights</Label>
                <Input
                  id="video_url"
                  name="video_url"
                  value={formData.video_url}
                  onChange={handleInputChange}
                  placeholder="YouTube, Vimeo, Wyscout, etc."
                  data-testid="scouting-video"
                />
                <p className="field-hint">Comparte un enlace con tus mejores jugadas o partidos completos</p>
              </div>

              <div className="form-group">
                <Label htmlFor="additional_info">Información adicional</Label>
                <Textarea
                  id="additional_info"
                  name="additional_info"
                  value={formData.additional_info}
                  onChange={handleInputChange}
                  placeholder="Cuéntanos sobre tu trayectoria, logros, objetivos profesionales..."
                  rows={4}
                  data-testid="scouting-info"
                />
              </div>
            </div>

            <div className="form-submit">
              <Button 
                type="submit" 
                className="btn-submit-scouting" 
                disabled={isSubmitting}
                data-testid="scouting-submit"
              >
                {isSubmitting ? (
                  'Enviando...'
                ) : (
                  <>
                    <Send className="btn-icon" />
                    ENVIAR PERFIL
                  </>
                )}
              </Button>
              <p className="submit-note">
                Al enviar, aceptas que evaluemos tu perfil y nos comuniquemos contigo si consideramos que hay oportunidad de colaboración.
              </p>
            </div>
          </form>
        </div>
      </main>

      <footer className="scouting-footer">
        <p>© 2025 Ikons Agency. Todos los derechos reservados.</p>
      </footer>
    </div>
  );
};

export default Scouting;
